import { OmneoImage, OmneoListItemInStore } from './omneo-wishlist-item'

export type GenericImage = {
  src: string
  alt: string
}

type RecordDates = {
  created_at: string
  updated_at: string
}

export type OmneoListDefinitionType = 'default' | 'store' | 'custom'

export type OmneoListDefinitionHandle = 'wishlist' | 'in-store' | 'piercing-appointment' | 'zendesk-ticket' | 'shared'

export type OmneoAuthTokenResSuccess = {
  data: {
    token: string
    exp: number
  }
}

export type OmneoAuthTokenResError = {
  error: boolean
  message: string
}

export type OmneoAuthHeaders = {
  Authorization: string
}

export type ShapesResponse = {
  status: number
  message: string
  error: boolean
  body: {
    data: unknown
  }
}

export type InterfaceShapesSdk = {
  find: Function
  hydrate: Function
  on: Function
  off: Function
  dispatch: Function
  get: Function
  post: Function
  put: Function
  delete: Function
  shares: Function
}

type OmneoListMeta = {
  image?: GenericImage
}

type OmneoShare = {
  id: number
  handle: string
  profile_id: string
  product_list_id: number
  created_at: string
  updated_at: string
}

type OmneoListDefinition = {
  id: number
  name: string
  handle: OmneoListDefinitionHandle
  type: OmneoListDefinitionType
  icon?: any
  cover?: any
  short_description: string
  description?: any
  allow_share: boolean
  allow_quantity: boolean
  allow_reserve: boolean
  allow_custom_product: boolean
  meta?: any
  is_published: boolean
  is_active: boolean
  tags: string[]
}

type OmneoListLocation = {
  id: number
  type: string
  name: string
  description?: any
  phone: string
  email: string
  external_id: string
  is_published: boolean
  is_permanently_closed: boolean
  address: OmneoListLocationAddress
}

type OmneoListLocationAddress = {
  id: number
  address_line_1: string
  address_line_2: string
  address_line_3?: any
  company?: any
  latitude: number
  longitude: number
  city: string
  postcode: string
  state: string
  country: string
  notes?: any
  external_id?: any
  is_default: boolean
  created_at: string
  updated_at: string
  meta?: any
  name?: string
  type?: any
  phone?: any
  iso: string
  iso_state: string
}

export type OmneoList = RecordDates & {
  id: number
  profile_id: string
  name: string
  description: null | string
  meta: null | (OmneoListMeta & { [name: string]: unknown })
  handle: string
  sort_order: null | string
  items: Array<OmneoListItem | OmneoListItemInStore>
  is_shared: boolean
  shares: Array<OmneoShare>
  tags: Array<string>
  custom_fields: Array<unknown>
  definition: OmneoListDefinition
  custom_date: null | unknown
  location: null | OmneoListLocation
  staff: null | unknown
  list_definition_id?: number
  lastUpdate?: string
}

export const omneoListEmpty: OmneoList = {
  id: 0,
  profile_id: '',
  name: '',
  description: null,
  meta: null,
  handle: '',
  sort_order: null,
  items: [],
  is_shared: false,
  shares: [],
  tags: [],
  custom_fields: [],
  definition: null as unknown as OmneoListDefinition,
  custom_date: null,
  location: null,
  staff: null,
  created_at: '',
  updated_at: ''
}

type OmneoProductCategory = {
  id: number
  name: string
}

type OmneoProductOptionValue = {
  id: number
  values: string
}

type OmneoProductOptions = {
  id: number
  handle: string
  name: string
  values: OmneoProductOptionValue[]
}

type OmneoVariantOption = {
  option_id: number
  value_id: number
}

type OmneoProductVariant = {
  id: number
  product_id: number
  title: string
  sku: string
  description: string
  available_quantity: number
  price: number
  price_discounted: number
  price_comparison: number
  price_cost: number
  position: number
  tags: string[]
  options: OmneoVariantOption[]
}

export type OmneoProduct = {
  id: number
  title: string
  handle: string
  external_id: string
  status: string
  brand: string
  description: string
  currency: string
  position: number
  systems: unknown[]
  channels: unknown[]
  images: Array<ProductImages>
  tags: string[]
  options: OmneoProductOptions[]
  variants: OmneoProductVariant[]
  images: OmneoImage[]
}

export type OmneoListItem = RecordDates & {
  custom_product?: unknown
  id: number
  meta: OmneoListItemMeta
  position: unknown
  product: OmneoProduct
  product_category: OmneoProductCategory
  product_list_id: number
  product_variant: OmneoProductVariant
  quantity?: null | number
  reservations?: unknown[]
  status?: null | unknown
}

export type OmneoListItemMeta = {
  productId: number
  variantId: number
  handle: string
  variantSku: string
  variantPrice: number // price in cents
  variantOptions: Array<string>
  variantAvailable: boolean
  templateSuffix: string | null
  productBiline: string
  image: string | GenericImage
  properties?: any
  productPersonalisedPrice?: string
}

export type OmneoSharedListRes = {
  data: RecordDates & {
    id: number
    handle: string
    profile_id: string
    product_list_id: number
  }
}

export type OmneoCommsPreferences = {
  email_account: boolean
  email_benefits: boolean
  email_bookings: boolean
  email_bounced: boolean
  email_discover: boolean
  email_feedback: boolean
  email_location: boolean
  email_optout: boolean
  email_promo: boolean
  email_reminders: boolean
  email_service: boolean
  email_verification: 'not_sent'
  phone_account: boolean
  phone_benefits: boolean
  phone_bookings: boolean
  phone_bounced: boolean
  phone_discover: boolean
  phone_feedback: boolean
  phone_location: boolean
  phone_optout: boolean
  phone_promo: boolean
  phone_reminders: boolean
  phone_service: boolean
  phone_verification: 'not_sent'
  post_account: boolean
  post_benefits: boolean
  post_bookings: boolean
  post_bounced: boolean
  post_discover: boolean
  post_feedback: boolean
  post_location: boolean
  post_optout: boolean
  post_promo: boolean
  post_reminders: boolean
  post_service: boolean
  post_verification: 'not_sent'
  profile_id: '919a7950-a01b-48a9-9291-2f36c328c935'
  push_account: boolean
  push_benefits: boolean
  push_bookings: boolean
  push_bounced: boolean
  push_discover: boolean
  push_feedback: boolean
  push_location: boolean
  push_optout: boolean
  push_promo: boolean
  push_reminders: boolean
  push_service: boolean
  sms_account: boolean
  sms_benefits: boolean
  sms_bookings: boolean
  sms_bounced: boolean
  sms_discover: boolean
  sms_feedback: boolean
  sms_location: boolean
  sms_optout: boolean
  sms_promo: boolean
  sms_reminders: boolean
  sms_service: boolean
  sms_verification: 'not_sent'
  terms_accepted_at: null
  terms_accepted_version: null
}

export type OmneoUpdateCommsPreferences = {
  email_promo: boolean
  email_service: boolean
  email_account: boolean
  email_optout: boolean
  sms_promo: boolean
  sms_service: boolean
  sms_account: boolean
  sms_optout: boolean
}

export type CommunicationPreferences = {
  error: {
    title: string
    content: string
  }
  success: {
    email_title: string
    email_promo: string
    email_service: string
    email_account: string
    email_optout: string
    sms_title: string
    sms_promo: string
    sms_service: string
    sms_account: string
    sms_optout: string
  }
}
